import { Skeleton } from "@/components/ui/baseUi/skeleton"
import { Text } from "@/components/ui/baseUi/text"
import { useInfiniteScroll } from "@/modules/core/hooks/useInfiniteScroll"
import { word } from "@/modules/core/utils/i18n"
import { ProfileAvatar } from "@/modules/profile/common/components/profileAvatar"
import type { UserEntity } from "@/modules/profile/userProfile/userEntity"
import { useSocialMentionableUsers } from "@/modules/social/hooks/useSocialMentionableUsers"

type UserMentionListProps = {
  socialPostId: string | null
  keyword: string
  onSelect: (user: UserEntity) => void
}

export function UserMentionList({ socialPostId, keyword, onSelect }: UserMentionListProps) {
  const { users, isLoading, isFetching, hasNextPage, fetchNextPage, isFetchingNextPage } = useSocialMentionableUsers(
    socialPostId,
    keyword,
  )
  const { InfiniteScrollTrigger } = useInfiniteScroll({ fetchNextPage, hasNextPage, isFetchingNextPage })

  if (isLoading || isFetching) {
    return (
      <div className="flex flex-col gap-2 p-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-2 px-2">
            <Skeleton className="size-8 shrink-0 rounded-full" />
            <Skeleton className="h-4 w-32" />
          </div>
        ))}
      </div>
    )
  }

  if (users.length === 0) {
    return (
      <div className="px-3 py-2">
        <Text fontStyle="BS" className="text-textOnSurface-subtle">
          {word("social.mention.noUsersFound")}
        </Text>
      </div>
    )
  }

  return (
    <div className="flex max-h-48 flex-col overflow-y-auto">
      <div className="px-3 py-1">
        <Text fontStyle="LS" className="text-textOnSurface-subtle">
          {word("social.mention.title")}
        </Text>
      </div>

      {users.map((user) => (
        <button
          key={user.id}
          onMouseDown={(e) => {
            e.preventDefault()
            onSelect(user)
          }}
          className="flex items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-interactive-background-neutral-faded"
        >
          <ProfileAvatar
            profile={{ avatar: user.avatar ?? null, username: user.username }}
            className="size-8 shrink-0"
          />
          <div className="flex flex-col">
            <Text fontStyle="BS" className="text-textOnSurface-default">
              {user.firstname} {user.lastname}
            </Text>
            <Text fontStyle="LS" className="text-textOnSurface-subtle">
              @{user.username}
            </Text>
          </div>
        </button>
      ))}

      <InfiniteScrollTrigger />
    </div>
  )
}
